<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div
      class="flex flex-col xl:flex-row lg:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <Heading :heading="graphHeading" heading-size="text-[18px]" heading-padding="py-0" />
        <div
          v-permission="PERIOD_ATTENDANCE_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse xl:flex-nowrap xl:items-center gap-2 items-end justify-end self-end xl:self-auto"
      >
        <UiFilterBox
          v-model="selectedClass"
          :options="classList"
          :is-switch-box="true"
          :is-disabled="isClassDisabled"
          label="title"
          reduce="id"
        />
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusList"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
        />
        <CalendarActionsWrapper
          :calendar-date="dateRange"
          :is-concise-view="true"
          :is-future-date-disabled="true"
          filter-view="Week"
          custom-class="right-0 before:right-12"
          @selectDate="updateSelectedDate"
          @selectDateRange="updateDateRange"
        />
      </div>
    </div>
    <div class="overflow-auto">
      <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
      <NoRecordFound v-else-if="isEmptyGraph" />
      <GraphView
        v-else
        :chart-data="graphData"
        :enable-datalabels="enableDatalabels"
        class="min-w-7"
      />
    </div>
    <div class="pb-6 flex mt-2">
      <UiFilterBox
        v-model="selectedSection"
        :options="sectionList"
        :is-disabled="isSectionDisabled"
        label="title"
        reduce="id"
        class="md:justify-end"
      />
      <UiDashboardStats title="Total Students:" :value="totalStudents" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import CalendarActionsWrapper from '@components/Calendar/CalendarActionsWrapper.vue'
import Heading from '@/src/components/Heading.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import icon from '@src/components/icons/icon.vue'
import Loader from '@src/components/BaseComponent/Loader.vue'
import { useStore } from 'vuex'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import UiDashboardStats from '@/src/components/UiElements/UiStatsCountDisplay.vue'
import { generateDayNames, getBackDate, formatDateFromLocalToUTC } from '@src/utils/moment.util.js'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'
import {
  fillEmptyTimeTable,
  createGraphData,
  findEarliestTime,
  transformScheduleData,
} from '@/src/router/views/dashboard/DashboardGraphs/DashboardGraph/time-table.util.js'
import { getCurrentScopeFlags } from '@/src/utils/level-scope.util.js'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import moment from 'moment'
import { ENV } from '@src/constants/general-constants.js'
// Vuex store
const store = useStore()
// State variables
const isLoading = ref(false)
const selectedCampus = ref('')
const totalStudents = ref('')
const dateRange = ref({
  startDate: getBackDate(moment().toDate(), 6, 'days'),
  endDate: moment().toDate(),
})
const graphHeading = 'Period Attendance'
const classList = ref([])
const sectionList = ref([])
const selectedClass = ref('')
const selectedSection = ref('')
const timeTable = ref([])
const enableDatalabels = true

// Computed properties
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const currentClassScope = computed(() => store.state.layout.currentClassScope)
const currentSectionScope = computed(() => store.state.layout.currentSectionScope)
const campusList = computed(() => store.state.layout.campuses)
const isClassDisabled = computed(() => !!currentClassScope.value)
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const isSectionDisabled = computed(() => !!currentSectionScope.value)
const weekNames = computed(() => generateDayNames(dateRange.value))
const isEmptyGraph = computed(() => {
  return graphData.data?.datasets?.every(({ data }) => !data?.length || data.every((item) => !item))
})
const PERIOD_ATTENDANCE_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}
const graphData = reactive({
  startTime: { Hours: 8, Minutes: 15 },
  type: 'bar',
  timetable: true,
  data: {
    labels: weekNames.value,
    datasets: [],
  },
  options: {
    responsive: true,
    maxBarThickness: 62,
    maintainAspectRatio: false,
    aspectRatio: 100 / 200,
    borderRadius: {
      topLeft: 10,
      topRight: 10,
      bottomLeft: 0,
      bottomRight: 0,
    },
    borderSkipped: false,
    scales: {
      x: {
        grid: { display: false },
        stacked: true,
        beginAtZero: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          stepSize: 30,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: false,
      datalabels: {
        align: 'start',
        anchor: 'end',
        color: 'white',
        font: {
          size: 12,
          weight: 500,
        },
      },
    },
  },
})

watch([currentCampusScope, currentClassScope, currentSectionScope], () => {
  fetchPeriodAttendanceStats()
})
watch(selectedCampus, async (newCampus, oldCampus) => {
  if (newCampus === oldCampus) return
  await fetchClassList(newCampus)
})
watch(selectedClass, async (newClass, oldClass) => {
  if (newClass === oldClass) return
  await fetchSectionList(newClass)
})
watch(
  [selectedSection, dateRange],
  async ([newSection, newDateRange], [oldSection, oldDateRange]) => {
    if (newSection === oldSection && newDateRange === oldDateRange) return
    await fetchPeriodAttendanceGraphData()
  },
)
onMounted(() => {
  fetchPeriodAttendanceStats()
})
// Methods
const fetchPeriodAttendanceStats = async () => {
  const { isCampusLevel, isClassLevel, isSectionLevel } = getCurrentScopeFlags()

  if (isCampusLevel) {
    selectedCampus.value = currentCampusScope.value.id
    await fetchClassList(selectedCampus.value)
    await fetchSectionList(selectedClass.value)
  } else if (isClassLevel) {
    selectedClass.value = currentClassScope.value.id
    await fetchClassList(selectedCampus.value)
    await fetchSectionList(selectedClass.value)
  } else if (isSectionLevel) {
    selectedSection.value = currentSectionScope.value.id
  }
  await fetchPeriodAttendanceGraphData()
}
const fetchPeriodAttendanceGraphData = async () => {
  if (currentCampusScope.value) {
    selectedCampus.value = currentCampusScope.value.id
  }
  if (currentClassScope.value) {
    selectedClass.value = currentClassScope.value.id
  }
  if (currentSectionScope.value) {
    selectedSection.value = currentSectionScope.value.id
  }
  if (!selectedSection.value) {
    graphData.data.datasets = []
    isLoading.value = false
    return
  }
  try {
    const graphPayload = {
      $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', selectedSection.value),
      from: formatDateFromLocalToUTC(dateRange.value.startDate, DATETIME_CONSTANT.DATE),
      to: formatDateFromLocalToUTC(dateRange.value.endDate, DATETIME_CONSTANT.DATE),
    }
    const response = await getPeriodAttendanceStats(graphPayload)
    timeTable.value = transformScheduleData(response.stats)
    const filledTimeTable = fillEmptyTimeTable(timeTable.value)
    graphData.startTime = findEarliestTime(timeTable.value)
    graphData.data.datasets = createGraphData(filledTimeTable)
    graphData.data.labels = weekNames.value
    totalStudents.value = response.students_strength
  } catch (error) {
    graphData.data.datasets = []
  } finally {
    isLoading.value = false
  }
}
const fetchClassList = async (campusId) => {
  isLoading.value = true
  try {
    const classPayload = {
      $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', campusId),
    }
    const classResponse = await getClasses(classPayload)
    classList.value = classResponse || []
    selectedClass.value = classResponse?.[0]?.id || ''
  } catch (error) {
    classList.value = []
    selectedClass.value = ''
  } finally {
    isLoading.value = false
  }
}
const fetchSectionList = async (classId) => {
  isLoading.value = true
  try {
    const sectionPayload = {
      $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'class_id', classId),
    }
    const sectionResponse = await getSections(sectionPayload)
    sectionList.value = sectionResponse.data || []
    selectedSection.value = sectionResponse.data?.[0]?.id || ''
    if (!sectionList.value.length) isLoading.value = false
  } catch (error) {
    sectionList.value = []
    selectedSection.value = ''
  } finally {
    isLoading.value = false
  }
}
const updateDateRange = (week) => {
  dateRange.value = week
}
const updateSelectedDate = (date) => {
  dateRange.value.startDate = date
}

//actions
const getSections = (payload) => store.dispatch('section/filterUsersSectionsList', payload)
const getPeriodAttendanceStats = (payload) =>
  store.dispatch('instituteCount/getPeriodAttendanceStats', payload)
const getClasses = (payload) => store.dispatch('classes/getUserClasses', payload)
</script>
